// src/pages/markets/board/state/usePreferences.ts
//
// Per-user, per-column board settings in Firestore at
// users/{uid}/preferences/screener_board.
//
// This is where filter state lives. The URL only carries a share link, applied
// once on open and never written back automatically.

import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetIdentity } from 'react-admin';
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import {
    GATED_FIELDS,
    FILTER_WIRE_KEYS,
    type BoardFilters,
    type TagMatch,
} from '../api/boardTypes';
import type { BodyMode, CardView } from '../board/columnConfig';
import type { ProfileRef } from './profileTypes';
import { filtersToStored } from './profileSerde';

const PREF_DOC_PATH = ['preferences', 'screener_board'] as const;
const PREF_VERSION = 1;
const WRITE_DEBOUNCE_MS = 500;

// Gated settings are stored under their backend key names, and the index
// signature is what keeps this from being a third place to list them.
export interface StoredColumn {
    events_include: string[];
    events_exclude: string[];
    tags_include: string[];
    tags_exclude: string[];
    tag_match: TagMatch;
    group: string | null;
    // The backend column this slot pulls from. Absent on documents written before categories shipped.
    source: string;
    sort_by: string;
    sort_dir: string;
    body: BodyMode | null;
    view: CardView | null;
    [gatedKey: string]: unknown;
}

export interface StoredPreferences {
    version: number;
    columns: Record<string, Partial<StoredColumn>>;
    active_profile_id?: ProfileRef | null;
    profile_order?: string[];
}

// Shared rather than built per call, so a hook reading it before the document
// arrives does not hand its consumers a new array on every render.
const EMPTY_ORDER: string[] = [];

const EMPTY_PREFERENCES: StoredPreferences = {
    version: PREF_VERSION,
    columns: {},
    active_profile_id: null,
    profile_order: EMPTY_ORDER,
};


// A missing key falls through to the seed rather than to null: a document
// written by an older build must not silently clear a filter the user never
// touched.
export function applyPreference(
    seed: BoardFilters,
    stored: Partial<StoredColumn> | undefined,
): BoardFilters {
    if (!stored) return seed;
    const base: BoardFilters = {
        ...seed,
        group: stored.group !== undefined ? stored.group : seed.group,
        sortBy: stored.sort_by ?? seed.sortBy,
        sortDir: (stored.sort_dir as BoardFilters['sortDir']) ?? seed.sortDir,
        eventsInclude: stored.events_include ?? seed.eventsInclude,
        eventsExclude: stored.events_exclude ?? seed.eventsExclude,
        tagsInclude: stored.tags_include ?? seed.tagsInclude,
        tagsExclude: stored.tags_exclude ?? seed.tagsExclude,
        tagMatch: stored.tag_match ?? seed.tagMatch,
    };
    const gated: Record<string, unknown> = {};
    for (const field of GATED_FIELDS) {
        // The seed decides whether this column has the field at all, so a
        // stored key for a column that no longer declares one is ignored.
        if (seed[field] === undefined) continue;
        const value = stored[FILTER_WIRE_KEYS[field]];
        if (value !== undefined) gated[field] = value;
    }
    return { ...base, ...gated } as BoardFilters;
}

export interface UsePreferencesResult {
    preferences: StoredPreferences;
    isLoaded: boolean;
    activeProfile: ProfileRef | null;
    profileOrder: string[];
    saveFilters: (column: string, filters: BoardFilters) => void;
    saveLayout: (column: string, patch: { body?: BodyMode; view?: CardView }) => void;
    saveSource: (column: string, source: string) => void;
    setActiveProfile: (ref: ProfileRef | null) => void;
    setProfileOrder: (ids: string[]) => void;
}

export function usePreferences(): UsePreferencesResult {
    const { identity, isLoading: identityLoading } = useGetIdentity();
    const userId = identity?.id ? String(identity.id) : null;
    const qc = useQueryClient();

    const queryKey = ['preferences', 'screener_board', userId ?? ''] as const;

    const query = useQuery<StoredPreferences>({
        queryKey,
        queryFn: async () => {
            const ref = doc(getFirestore(), 'users', userId as string, ...PREF_DOC_PATH);
            const snap = await getDoc(ref);
            if (!snap.exists()) return EMPTY_PREFERENCES;
            const data = snap.data() as Partial<StoredPreferences>;
            return {
                version: data.version ?? PREF_VERSION,
                columns: data.columns ?? {},
                active_profile_id: data.active_profile_id ?? null,
                profile_order: data.profile_order ?? EMPTY_ORDER,
            };
        },
        enabled: Boolean(userId),
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
    });

    const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

    useEffect(() => {
        const pending = timers.current;
        return () => {
            for (const t of pending.values()) clearTimeout(t);
            pending.clear();
        };
    }, []);

    const write = useCallback(
        (column: string, patch: Partial<StoredColumn>) => {
            if (!userId) return;

            const current = qc.getQueryData<StoredPreferences>(queryKey) ?? EMPTY_PREFERENCES;
            const merged = { ...(current.columns[column] ?? {}), ...patch };
            if (JSON.stringify(merged) === JSON.stringify(current.columns[column])) return;

            // Spread current: a fresh object here would drop active_profile_id and
            // deselect the profile on the first filter write after choosing it.
            qc.setQueryData<StoredPreferences>(queryKey, {
                ...current,
                version: PREF_VERSION,
                columns: { ...current.columns, [column]: merged },
            });

            const existing = timers.current.get(column);
            if (existing) clearTimeout(existing);

            timers.current.set(
                column,
                setTimeout(() => {
                    timers.current.delete(column);
                    const ref = doc(getFirestore(), 'users', userId, ...PREF_DOC_PATH);
                    // Merge on the map key, so a write for one column never
                    // overwrites the other three.
                    void setDoc(
                        ref,
                        { version: PREF_VERSION, columns: { [column]: merged } },
                        { merge: true },
                    ).catch(err => {
                        console.warn('Could not save board settings', err);
                    });
                }, WRITE_DEBOUNCE_MS),
            );
        },
        [qc, queryKey, userId],
    );

    const saveFilters = useCallback(
        (column: string, filters: BoardFilters) => write(column, filtersToStored(filters)),
        [write],
    );

    const saveLayout = useCallback(
        (column: string, patch: { body?: BodyMode; view?: CardView }) => write(column, patch),
        [write],
    );

    const saveSource = useCallback(
        (column: string, source: string) => write(column, { source }),
        [write],
    );

    // Written immediately, and never on the column debounce: the two writers must
    // not share a timer or one would clobber the other's merge.
    const setActiveProfile = useCallback(
        (ref: ProfileRef | null) => {
            if (!userId) return;
            const current = qc.getQueryData<StoredPreferences>(queryKey) ?? EMPTY_PREFERENCES;
            qc.setQueryData<StoredPreferences>(queryKey, { ...current, active_profile_id: ref });
            const docRef = doc(getFirestore(), 'users', userId, ...PREF_DOC_PATH);
            void setDoc(docRef, { active_profile_id: ref }, { merge: true }).catch(err => {
                console.warn('Could not save the active profile', err);
            });
        },
        [qc, queryKey, userId],
    );

    // Immediate too, and for the same reason: a drop is a deliberate act, and the
    // column debounce is not its timer.
    const setProfileOrder = useCallback(
        (ids: string[]) => {
            if (!userId) return;
            const current = qc.getQueryData<StoredPreferences>(queryKey) ?? EMPTY_PREFERENCES;
            qc.setQueryData<StoredPreferences>(queryKey, { ...current, profile_order: ids });
            const docRef = doc(getFirestore(), 'users', userId, ...PREF_DOC_PATH);
            void setDoc(docRef, { profile_order: ids }, { merge: true }).catch(err => {
                console.warn('Could not save the profile order', err);
            });
        },
        [qc, queryKey, userId],
    );

    return {
        preferences: query.data ?? EMPTY_PREFERENCES,
        // A signed-out user has nothing to load, so the board must not wait.
        isLoaded: !userId ? !identityLoading : query.isSuccess || query.isError,
        activeProfile: query.data?.active_profile_id ?? null,
        profileOrder: query.data?.profile_order ?? EMPTY_ORDER,
        saveFilters,
        saveLayout,
        saveSource,
        setActiveProfile,
        setProfileOrder,
    };
}